import { useParams } from 'react-router-dom';
import { Flex } from '@theme-ui/components';
import { useQuery } from '@apollo/client';
import { useTheme } from '@emotion/react';

import Loader from '@app/components/Loader';
import NotFound from '@app/components/NotFound';
import useLocationSearch from '@app/hooks/useLocationSearch';
import { DIRECTUS_SUPER_CLIENT_URL_KEY } from '@config/common.config';

import useLocaleWithFallback from '../../hooks/useLocaleWithFallback';
import type {
  PricingPage,
  PricingPageVariables,
} from './__generated__/PricingPage';
import SingleProductLayout from './SingleProduct/SingleProductLayout';
import MultiProductLayout from './MultiProductLayout';
import PRICING_PAGE_QUERY from './Pricing.query';
import PricingMeta from './PricingMeta';

export const SINGLE_PRODUCT_LAYOUT_NAME = 'pricing_pages_single_layout';
const MULTI_PRODUCT_LAYOUT_NAME = 'pricing_pages_multi_layout';

const Pricing = () => {
  const theme = useTheme();
  const { url_key } = useParams<{
    url_key: string;
  }>();

  const directusSuperClientToken = useLocationSearch(
    DIRECTUS_SUPER_CLIENT_URL_KEY,
  );
  const { getLanguagesWithFallback } = useLocaleWithFallback();

  const { data, loading, error } = useQuery<PricingPage, PricingPageVariables>(
    PRICING_PAGE_QUERY,
    {
      variables: {
        languages: getLanguagesWithFallback(),
        url_key,
      },
      context: {
        directusSuperClientToken,
      },
    },
  );

  const pricingPageData = data?.content?.pricing_pages?.[0];

  if (loading) {
    return (
      <Flex py={4} sx={{ justifyContent: 'center' }}>
        <Loader color={theme.colors.lighterBlue} size={30} />
      </Flex>
    );
  }

  if (error) {
    throw new Error('Unable to query pricing page');
  }

  if (!pricingPageData) {
    return <NotFound errorMessage="Pricing page data is missing" />;
  }

  const { layout, translations } = pricingPageData;
  const layoutItem = layout?.[0]?.item;
  const metaData = translations?.[0];

  if (!layoutItem) {
    return (
      <NotFound errorMessage="No layout is specified for this pricing page" />
    );
  }

  switch (layoutItem.__typename) {
    case SINGLE_PRODUCT_LAYOUT_NAME:
      return (
        <>
          <PricingMeta metaData={metaData} />
          <SingleProductLayout layout={layoutItem} />
        </>
      );
    case MULTI_PRODUCT_LAYOUT_NAME:
      return (
        <>
          <PricingMeta metaData={metaData} />
          <MultiProductLayout layout={layoutItem} />
        </>
      );
    default:
      return <NotFound errorMessage={'Unsupported layout type'} />;
  }
};

export default Pricing;
